import { makeStyles } from "@material-ui/core";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import CalendarEntryContentView from "Components/Calendar/CalendarEntryContentView";
import CalendarEntryDraggableView from "Components/Calendar/CalendarEntryDraggableView";
import {
	CALENDAR_ENTRY_HEIGHT,
	CALENDAR_ENTRY_TYPE_HEIGHT,
	CalendarEntry,
	TABLE_CELL_MARGIN_Y,
	useDraggedItem,
} from "Components/Calendar/CalendarEntryHelper";
import { CellSize } from "Components/Calendar/CalendarHelper";
import {
	useEnabledWorkingDaysLength,
	useGetEnabledDaysDiff,
	useIsIsoWeekdayEnabled,
} from "Components/CompanySetting/CompanySettingHelper";
import { useIsInternalAdmin } from "Components/Members/hooks/useIsInternalAdmin";
import { ResizePixelSet } from "Components/Resize/ResizeHelper";
import ResizeView, { ResizeDirection } from "Components/Resize/ResizeView";
import { px } from "Configs/theme";
import { useIsBrowserOrIpadOs } from "Helpers/PlatformHelper";
import { ValidData } from "Store/types/ValidData";
import { Project } from "domain-entities";
import moment, { Moment } from "moment";
import React, { CSSProperties, FC, Fragment, useState } from "react";
import { isMonthMergedCalendarEntryCutOff } from "./CalendarMonthHelper";

interface Props {
	calendarEntry: CalendarEntry;
	calendarEntries: CalendarEntry[]; // from `MergedCalendarEntry`
	colorHex: string;
	projects: Project[];
	dropType: string;
	startOfCalendar: Moment;
	endOfCalendar: Moment;
	cellSize: CellSize;
	resizePixelSet: ResizePixelSet;
	colorHexes?: string[];
	isNew?: boolean;
	isFullDay?: boolean;
	selectCalendarEntry: () => void;
	onResizeCalendarEntry: (
		calendarEntry: Partial<CalendarEntry> & ValidData
	) => Promise<void>;
	tooltip: JSX.Element | null;
}

const CalendarMonthEntryView: FC<Props> = (props) => {
	const classes = useStyles();
	const isInternalAdmin = useIsInternalAdmin();
	const isBrowserOrIpadOs = useIsBrowserOrIpadOs();
	const isIsoWeekdayEnabled = useIsIsoWeekdayEnabled();
	const getEnabledDaysDiff = useGetEnabledDaysDiff();
	const [isResizing, setIsResizing] = useState(false);
	const [resizeWidth, setResizeWidth] = useState(0);
	const [resizeLeft, setResizeLeft] = useState(0);
	const startTime = moment.unix(props.calendarEntry.startTime);
	const endTime = moment.unix(props.calendarEntry.endTime);
	const dayWidth = props.cellSize.month.dayWidth;
	const enabledWorkingDaysLength = useEnabledWorkingDaysLength();
	const isEndTimeAfterEndOfCalendar = endTime.isAfter(props.endOfCalendar);
	const isStartTimeBeforeStartOfCalendar = startTime.isBefore(
		props.startOfCalendar
	);

	const earliestEndTime = isEndTimeAfterEndOfCalendar
		? props.endOfCalendar
		: endTime;

	const latestStartTime = isStartTimeBeforeStartOfCalendar
		? props.startOfCalendar
		: startTime;

	const {
		left: isMergedCalendarEntryCutOffLeft,
		right: isMergedCalendarEntryCutOffRight,
	} = isMonthMergedCalendarEntryCutOff({
		calendarEntry: props.calendarEntry,
		calendarEntries: props.calendarEntries,
	});

	const isCutOffLeft =
		isStartTimeBeforeStartOfCalendar ||
		isMergedCalendarEntryCutOffLeft ||
		!isIsoWeekdayEnabled(startTime.isoWeekday());

	const isCutOffRight =
		isEndTimeAfterEndOfCalendar ||
		isMergedCalendarEntryCutOffRight ||
		!isIsoWeekdayEnabled(
			props.isFullDay
				? endTime.clone().subtract(1, "day").isoWeekday()
				: endTime.isoWeekday()
		);

	const width = getWidth();
	const left = getLeft();
	const { isDragging, drag, preview } = useDraggedItem(
		props.dropType,
		props.calendarEntry,
		props.colorHex,
		props.isNew
	);

	function getLeft() {
		if (isStartTimeBeforeStartOfCalendar) {
			return 0;
		} else {
			const enabledDaysDiff = getEnabledDaysDiff(
				props.startOfCalendar,
				startTime
			);

			return Math.round(dayWidth * enabledDaysDiff);
		}
	}

	function getWidth() {
		const enabledDaysDiff = getEnabledDaysDiff(
			latestStartTime,
			props.isFullDay ? earliestEndTime : earliestEndTime.clone().add(1, "day")
		);

		return Math.round(dayWidth * enabledDaysDiff) - 1;
	}

	function getStyle() {
		const x = left + resizeLeft;
		const y = props.calendarEntry.projectId
			? TABLE_CELL_MARGIN_Y
			: CALENDAR_ENTRY_HEIGHT -
			  CALENDAR_ENTRY_TYPE_HEIGHT +
			  TABLE_CELL_MARGIN_Y;

		const style: CSSProperties = {
			transform: `translate3d(${x + 1}px, ${y}px, 0)`,
			width: width + resizeWidth,
			zIndex: isResizing ? 2 : 1,
		};

		return style;
	}

	function renderResizeView(resizeDirection: ResizeDirection) {
		if (
			isInternalAdmin &&
			isBrowserOrIpadOs &&
			((!isCutOffLeft && resizeDirection === "left") ||
				(!isCutOffRight && resizeDirection === "right"))
		) {
			return (
				<ResizeView
					columnWidth={props.cellSize.month.dayWidth * enabledWorkingDaysLength}
					calendarEntry={props.calendarEntry}
					calendarEntryWidth={width}
					calendarEntryLeft={
						props.cellSize.offset.left + props.cellSize.month.nameWidth + left
					}
					resizeDirection={resizeDirection}
					resizePixels={props.resizePixelSet[resizeDirection]}
					isResizing={isResizing}
					isTooltipVisible={false}
					isFullDay={props.isFullDay}
					setIsResizing={setIsResizing}
					setResizeWidth={setResizeWidth}
					setResizeLeft={setResizeLeft}
					selectScheduleEntry={props.selectCalendarEntry}
					onChange={(dateTime) =>
						props.onResizeCalendarEntry({
							id: props.calendarEntry.id,
							[resizeDirection === "left" ? "startTime" : "endTime"]: dateTime,
						})
					}
				/>
			);
		}
	}

	function renderContent() {
		if (props.colorHexes && props.colorHexes.length > 1) {
			return <ZoomInIcon className={classes.zoom} />;
		} else {
			return (
				<Fragment>
					{renderResizeView("left")}
					<CalendarEntryContentView
						isDragging={isDragging}
						calendarEntry={props.calendarEntry}
						projects={props.projects}
						drag={drag}
					/>
					{renderResizeView("right")}
				</Fragment>
			);
		}
	}

	if (width > 0) {
		return (
			<CalendarEntryDraggableView
				calendarEntry={props.calendarEntry}
				colorHex={props.colorHex}
				colorHexes={props.colorHexes}
				isDragging={isDragging}
				isResizing={isResizing}
				onClick={props.selectCalendarEntry}
				preview={preview}
				style={getStyle()}
				isCutOff={{
					left: isCutOffLeft,
					right: isCutOffRight,
				}}
				tooltip={props.tooltip}
			>
				{renderContent()}
			</CalendarEntryDraggableView>
		);
	} else {
		return null;
	}
};

export default CalendarMonthEntryView;

const useStyles = makeStyles({
	zoom: {
		display: "none",
		fontSize: px.xl,
	},
});
